import {FC, ReactNode} from 'react';
import {Box, Typography} from '@mui/material';
import {MoneyAmount} from 'src/utils/reportMoney';
import {kpiCardSx} from './KpiCard.sx';

interface KpiCardProps {
    label: string;
    /**
     * The figure - or several, when the card shows money a report holds in more than one currency.
     *
     * Each one is drawn in the same type as a single figure, one per line, so a card showing one
     * currency and a card showing three are read the same way. They used to be joined into one string
     * and handed over as `value`, which put the currency codes in the headline type and stretched the
     * total past the width of the card, while the same card with one currency showed a figure with a
     * small unit beside it. Two different-looking cards for the same quantity.
     */
    value: string | MoneyAmount[];
    /** The unit beside a single figure. A list carries its own, one per entry. */
    unit?: string;
    icon: ReactNode;
    loading?: boolean;
    /**
     * The phone's headline figure: bigger type, no icon competing with it.
     *
     * Off by default, so every other screen that borrows this card renders exactly as it did.
     */
    hero?: boolean;
}

export const KpiCard: FC<KpiCardProps> = ({label, value, unit, icon, loading, hero}) => {
    // One shape to render, whichever way the caller said it.
    const amounts: MoneyAmount[] = typeof value === 'string' ? [{value, unit}] : value;
    const rowSx = hero ? kpiCardSx.heroValueRow : kpiCardSx.valueRow;

    return (
        <Box sx={hero ? {...kpiCardSx.root, ...kpiCardSx.heroRoot} : kpiCardSx.root}>
            <Box sx={kpiCardSx.header}>
                {!hero && <Box sx={kpiCardSx.iconBox}>{icon}</Box>}
                <Typography sx={hero ? kpiCardSx.heroLabel : kpiCardSx.label}>{label}</Typography>
            </Box>
            {loading ? (
                <Box sx={rowSx}>
                    <Box sx={kpiCardSx.skeleton} />
                </Box>
            ) : (
                <Box sx={kpiCardSx.amounts}>
                    {amounts.map((amount) => (
                        <Box key={`${amount.value}-${amount.unit ?? ''}`} sx={rowSx}>
                            <Typography sx={hero ? kpiCardSx.heroValue : kpiCardSx.value}>
                                {amount.value}
                            </Typography>
                            {amount.unit && (
                                <Typography sx={hero ? kpiCardSx.heroUnit : kpiCardSx.unit}>
                                    {amount.unit}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
